/* Built In Imports */
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

/* External Imports */
import { Box, Flex, Text } from '@chakra-ui/react';
import LiteYouTubeEmbed from '@components/Embeds/YoutubeEmbed';
import { getEmbedUrl } from '@components/Utility/utils';
import { AiOutlinePlayCircle } from 'react-icons/ai';
import { CgNotes } from 'react-icons/cg';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import BackIcon from '@lb/components/Icons/BackIcon';
import ShareIcon from '@lb/components/Icons/ShareIcon';
import config from 'config';
/* Shared files */
import months from '@lb/components/SharedFiles/months';
import Image from 'next/image';

/**
 * Returns the Top Banner Event .
 *
 * @param {object} pageData - Content from API
 * @param {string} region - Region of page
 * @param {string} language - Lang of page
 * @param pageData.src
 * @param pageData.width
 * @param pageData.quality
 * @returns {ReactElement} TopBannerEvent component
 */
const imageLoader = ({ src, width, quality }) => {
  return `${src}?q=${quality || 20}`;
};
const TopBannerBlog = ({ pageData, region, language, pageName }) => {
  const [opts, setOpts] = useState({});
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const currentUrl = useRouter();
  const url = currentUrl.asPath;
  let date = new Date(pageData?.createdAt);
  let blogType = pageData?.blogContentType;

  const authorSchema =
    pageData.authorStructuredData === 'Sadhguru'
      ? [
          {
            '@type': 'Person',
            name: 'Sadhguru Jaggi Vasudev',
            url: 'https://isha.sadhguru.org',
            sameAs: 'https://www.instagram.com/sadhguru/?hl=en',
            identifier: 'https://www.youtube.com/c/sadhguru',
          },
        ]
      : [
          {
            '@type': 'Organization',
            name: 'Isha Foundation',
            url: 'https://www.instagram.com/isha.foundation/?hl=en',
            logo: '',
            sameAs: [
              'https://www.facebook.com/ishafoundation/',
              'https://twitter.com/ishafoundation',
              'https://www.instagram.com/isha.foundation/?hl=en',
              'https://www.youtube.com/channel/UCgaiWfiix1zaQS6Mn5SIw2g',
            ],
          },
        ];

  const schemaData = pageData.video?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: pageData.video[0]?.video?.title,
        thumbnailUrl: pageData.video[0]?.video?.thumbnailUrl,
        description: pageData.video[0]?.video?.description,
        author: authorSchema,
        uploadDate: pageData.video[0]?.video?.publishedAt,
        duration: pageData.video[0]?.video?.duration,
      }
    : null;

  useEffect(() => {
    setOpts({
      height: isMobile ? '202' : '420',
      width: '100%',
      playerVars: {
        autoplay: 1,
      },
    });
  }, [global?.window]);
  return (
    <Box
      maxW="1330px"
      w="100%"
      pos="relative"
      mx="auto"
      width="100%"
      id="BannerContainer"
    >
      <Box
        bg="#670900"
        _before={{
          content: "''",
          left: '0',
          top: '0',
          bottom: '0',
          right: '0',
          h: '100%',
          w: '100%',
          opacity: '0.4',
          position: 'absolute',
          backgroundImage: `url(${config.imgPath}/d/46272/1682095879-white-texture-final.png)`,
          zIndex: '0',
          mixBlendMode: 'overlay',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
        _after={{
          content: '""',
          position: 'absolute',
          maxW: '236px',
          width: '20%',
          height: { base: '70px', md: '150px' },
          top: '100%',
          zIndex: 0,
          left: '0',
          background: 'bannerGradient.bannerOrange',
          transform: 'matrix(-1, 0, 0, 1, 0, 0)',
          backgroundBlendMode: '',
        }}
        backgroundSize="cover"
        backgroundPosition="center"
        h="100%"
        display="flex"
        alignItems="flex-end"
        justifyContent="flex-end"
        position="relative"
        zIndex="2"
        pt="0"
        px={{ base: '0', md: '0' }}
        mixBlendMode="multiply"
      >
        <Box
          w={{ base: '100%', md: '100%', lg: '80%' }}
          maxW="904px"
          margin={{ base: '0 0px -20px', md: '0 auto -40px' }}
          top={{ base: 'auto', md: '0px' }}
          pos="relative"
          display="grid"
          placeItems="start"
          zIndex={{ base: '-1', md: '3' }}
          mixBlendMode="hard-light"
        >
          <Box
            w="100%"
            background="linear-gradient(90deg, rgba(210, 100, 26, 0.8) 0%, rgba(210, 100, 26, 0) 91.15%)"
            mixBlendMode="multiply"
            padding={{
              base: '80px 16px 103.18px',
              md: '114px 16px 220px 16px',
              lg: '114px 0 220px',
            }}
          >
            <Box w="100%" height="auto" maxW={696} mx="auto">
              {pageData?.title && (
                <Text
                  fontSize={{ base: '32px', sm: '32px', md: 'h5', lg: '46px' }}
                  as="h1"
                  m={0}
                  lineHeight={{
                    base: '38px',
                    sm: '38px',
                    md: '60px',
                    lg: '75px',
                    xl: '49px',
                  }}
                  color="title.light"
                  maxW={{ base: '270px', md: '652px', lg: '652px' }}
                  fontFamily="FedraSerifDisplay"
                >
                  {pageData?.title}
                </Text>
              )}
              <Text
                fontFamily="FedraSerifAStdBook"
                fontStyle="normal"
                fontWeight="350"
                fontSize={{ base: 'body2', md: '20px' }}
                lineHeight={{ base: '26px', md: '32px' }}
                mt={'20px'}
                color="bodyText.color3"
              >
                {pageData.summary}
              </Text>
              <style>
                {`
              .before:before{
                height:150px;
                bottom:-150px;
              }
                  #event-description p{
                    font-family: 'FedraSansStd-book';
                    font-style: normal;
                    font-weight: 400;
                    font-size: 24px;
                    line-height: 120%;
                    margin-top:10px;
                    color: #FFCB66;
                  }
                  @media (max-width:730px){
                    .before:before{
                        height:70px;
                        width:70px;
                        bottom:-70px;
                      }
                  }
                  @media (max-width:660px){
                    #event-description p{
                      font-family: 'FedraSansStd-book';
                    font-style: normal;
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 120%;
                    margin-top:7px;
                    color: #FFCB66;
                    }
                  }
                  @media (max-width:768px){
                    .topBannerBlogEmbed iframe{
                      height:202px;
                    }
                    }
                  `}
              </style>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        position="relative"
        maxW="696px"
        w={{ base: 'calc(100% - 32px)', md: '100%' }}
        zIndex={5}
        mx="auto"
        px={{ base: '16px', md: '0', lg: '0' }}
        marginTop={{ base: '-70', md: '-165px' }}
        h={{ base: '206px', sm: '300px', md: '350px', lg: '418px' }}
      >
        <Box h="100%" w="100%">
          {pageData?.blogContentType === 'article' && (
            <Image
              placeholder="blur"
              quality={100}
              fill
              sizes="100vw"
              style={{
                objectFit: 'cover',
              }}
              // loader="https://static.sadhguru.org/d/46272/1682095879-white-texture-final.png"
              h="100%"
              priority
              // blurDataURL={imageLoader(pageData?.heroImage?.url)}
              // blurDataURL={`${pageData?.heroImage?.url}?q=20`}
              blurDataURL="https://static.sadhguru.org/d/46272/1682095879-white-texture-final.png"
              w="100%"
              alt={pageData?.heroImage?.alt}
              src={pageData?.heroImage?.url}
            />
          )}
          {pageData?.blogContentType === 'video' && (
            <Box
              maxW="100%"
              w="full"
              maxH={{
                base: '206px',
                sm: '300px',
                md: '350px',
                lg: '418px',
              }}
              className="topBannerBlogEmbed"
            >
              <LiteYouTubeEmbed
                id={getEmbedUrl(pageData?.video[0]?.video?.url)}
                embedUrl={getEmbedUrl(pageData?.video[0]?.video?.url)}
                cookie
              />
            </Box>
          )}
        </Box>
      </Box>
      <Flex
        alignSelf="flex-start"
        width={{ base: 'auto', md: '100%' }}
        position="absolute"
        top="45px"
        left={{ base: '20px', md: '26px', lg: '60px' }}
        maxW="696px"
        mx="auto"
        right={{ base: 'auto', md: 'auto', lg: '60px' }}
        zIndex="10"
        justifyContent="space-between"
      >
        <Box display={{ base: 'none', md: 'flex' }}>
          <BackIcon region={region} language={language} />
        </Box>
        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent={{ base: 'flex-start', md: 'center' }}
          w="auto"
          height="auto"
          gridGap={{ base: '28px', md: '22px' }}
        >
          <Text
            as="span"
            color="#F5D5A9"
            fontSize={{ base: 'body3', md: 'body1' }}
            lineHeight="26px"
            style={{ fontFeatureSettings: 'case on' }}
            fontStyle="normal"
            fontFamily="FedraSansStd-Book"
            fontWeight="400"
          >
            {date?.getDate()}&nbsp;
            {months[date.getMonth()]} {date.getFullYear()} |
            {blogType === 'video'
              ? ` ${pageData?.video[0].video?.duration?.split(':')[0]} min`
              : ''}
            {blogType === 'article' &&
              pageData?.bodyWordCounter?.readingTime
                ?.replace('~', '')
                ?.replace('0 seconds', ' 1 minute')
                ?.replace('seconds', 'sec')}
          </Text>
          <Box className="label">
            <Box
              display="flex"
              alignItems="center"
              gridGap="7px"
              w="auto"
              height="auto"
              maxH="26px"
              p="6px 10px"
              bg="#DB3030"
              borderRadius="29px"
              color="#ffffff"
              justifyContent="center"
            >
              <Text
                as="span"
                fontSize="12px"
                lineHeight="16px"
                fontFamily="FedraSansStd-Book"
                fontStyle="normal"
                fontWeight="500"
              >
                {blogType == 'video' && <AiOutlinePlayCircle />}
                {blogType == 'article' && <CgNotes />}
              </Text>
              <Text
                as="span"
                fontSize="12px"
                lineHeight="16px"
                fontFamily="FedraSansStd-Book"
                fontStyle="normal"
                fontWeight="500"
              >
                {blogType == 'video' && 'Video'}
                {blogType == 'article' && 'Article'}
              </Text>
            </Box>
          </Box>
        </Flex>
        <Box display={{ base: 'none', md: 'flex' }}>
          <ShareIcon url={url} />
        </Box>
      </Flex>
      <Flex
        pos="absolute"
        top="0"
        right={0}
        display={{ base: 'flex', md: 'none' }}
        left="0"
        padding="15px"
        width="100%"
        zIndex="15"
        h="auto"
        alignItems="center"
        bg="linear-gradient(180deg, rgba(0, 0, 0, 0.42) 0%, rgba(0, 0, 0, 0) 100%)"
        justifyContent="space-between"
        color="#F5D5A9"
      >
        <Box fontSize="18px" cursor="pointer" pos="relative" zIndex={10}>
          <BackIcon background="none" region={region} language={language} />
        </Box>
        <Box fontSize="18px" cursor="pointer" pos="relative" zIndex={10}>
          <ShareIcon background={'none'} url={url} />
        </Box>
      </Flex>
      {schemaData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      )}
    </Box>
  );
};

export default TopBannerBlog;
